/**
 * Single source of truth for all search filter constants.
 *
 * Every component / hook that needs region options, category mappings,
 * or default filter values imports from here.
 */
import type {
  SBADocType,
  SearchRegion,
  SearchModeValue,
  SearchFilterOptions,
} from '../types';
import {
  isCompetitiveIntelEnabled,
} from '../config/featureFlags';

// ── Region options (order = UI display order) ──────────────────────────
export const REGION_OPTIONS: { value: SearchRegion; label: string }[] = [
  { value: 'US', label: 'US' },
  // US-Drugs@FDA: admin-only until opened (see ADMIN_ONLY_REGIONS / isUsFdaVisible).
  { value: 'US_FDA', label: 'US-Drugs@FDA' },
  { value: 'EU', label: 'EU' },
  { value: 'UK', label: 'UK' },
  { value: 'CA', label: 'CA' },
  { value: 'AU', label: 'AU' },
  { value: 'JP', label: 'JP' },
  // CN is admin-only + Investigator-only; the pill is hidden elsewhere via
  // ADMIN_ONLY_REGIONS / isRegionVisible. It stays in REGION_OPTIONS so the
  // registry-parity test (options - DISABLED == region_enabled) holds and
  // admins get a rendered/labelled pill on the Investigator surface.
  { value: 'CN', label: 'CN' },
  // CH (Swiss ch_pdf) / DE (German BfArM de_pdf) / FR (French ANSM fr_html):
  // launched to all users 2026-10-27 on Cogni, Keyword and Investigator.
  { value: 'CH', label: 'Swiss' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  // IE (Irish HPRA ie_pdf) / IT (Italian AIFA it_pdf): admin-only canaries,
  // Cogni only like DE (see isRegionVisible).
  { value: 'IE', label: 'Ireland' },
  { value: 'IT', label: 'Italy' },
];

// Regions whose filter is temporarily non-interactable. Pills/checkboxes
// still render but are greyed and show the tooltip below on hover. Empty
// the set when the underlying data work is ready.
//
// UK: SmPC HTML data on prod has known quality gaps; keep greyed for
//     non-admins.  Admins bypass the greyout to spot-check.
// CA + AU: un-greyed 2026-05-27 — family-table PDFs now wired through
//     Cogni search + PDF viewer end-to-end (see today's deploy:
//     family-aware retrieval + CA PM SOR-metadata relink).
// JP: un-greyed 2026-06-22 — PMDA XML labels ingested + translated to English
//     (leak/table/escape cleanup certified 100% clean), wired through Cogni /
//     Q&A / Compare / Investigator (JP_XML_FAMILY) end-to-end.
// UK un-gated 2026-05-29 along with the CA/AU + HA Reviews rollout —
// UK SmPC (sections region='UK') + UK PIL (uk_pil_evidence_chunks)
// keyword search both shipped that day. (UK stays greyed: SmPC HTML quality gaps.)
//
// COMING SOON (2026-10-27): CH / DE / FR / IE / IT — parsed + viewer built, not
// live for everyone yet. Rendered GREYED with "Coming soon" on Cogni AND Keyword
// for every user (see isRegionVisible); admins click through on Cogni where
// it already serves the region (ADMIN_BYPASSABLE_REGIONS). WHEN A REGION GOES
// LIVE on Cogni: delete it here AND from ADMIN_BYPASSABLE_REGIONS (and from
// ADMIN_ONLY_REGIONS + its backend *_ADMIN_ONLY flag once live everywhere).
// IE / IT launched on Cogni 2026-10-31 (IE_PDF / IT_PDF _ADMIN_ONLY default 0 in compose).
export const DISABLED_REGIONS: ReadonlySet<SearchRegion> = new Set(['UK']);

// Keyword search regions. Non-US RE-OPENED 2026-06-04 for the cross-geo compare
// (phase 2). The 2026-06-03 re-greying was specifically about the non-US section
// MATRIX (compare-grid-regulatory), which assembles poorly-parsed CA-PM text —
// that path stays US-only via the all-US gate in CompareKeywordPage. The
// side-by-side N-column compare renders the REAL PDF via the regulatory viewers
// (scrollToSection), not the parsed text, so it's unaffected by parse quality.
// JP un-greyed 2026-06-22 — jp_chunk_phrase_index sidecar + bitmap built, all
// match types (boolean/exact/wildcard/stemmed) wired & smoke-tested on prod.
// CN added 2026-08-23: zh keyword search feeds Compare, which isn't wired for
// Chinese yet — so CN renders GREYED on the Keyword surface (visible to admins,
// non-clickable) and is NOT in KEYWORD_ADMIN_BYPASSABLE_REGIONS, so even admins
// can't click through until compare-view work lands. Remove CN here to un-grey.
//
// UK un-greyed on the KEYWORD surface 2026-10-27 — open to all users.
// It was greyed for SmPC HTML parse quality, NOT for a broken search path, and
// the search path was re-verified against staging before opening it (real
// service, three match types, UK-scoped):
//     stemmed single term  5,356 hits   746ms
//     exact phrase         7,130 hits    19.3s
//     boolean AND (2 rows) 1,064 hits     4.8s
// all returning real UK drugs, and faster than the same US queries on exact
// phrase. Postings back this: the `label` corpus is built
// ``region IN ('US','UK')`` (keyword_postings_maintenance.py:124,169) and
// ``_CORPUS_VALID_REGIONS = {"label": ("US","UK")}``, so UK rides the FTS /
// sidecar path — NOT the monolithic UK vector step.
//
// Parse quality is a KNOWN, ACCEPTED trade here (product decision, 2026-10-27):
// UK SmPC HTML still has the gaps the old "Updating ..." tooltip referred to, so
// expect rough section text in results. That is deliberate, not a regression.
//
// UK REMAINS greyed on cogni/research (``DISABLED_REGIONS`` above, not
// bypassable even for admins) because UK label retrieval HANGS the research
// worker — the monolithic UK vector step never returns, and the backend rejects
// UK research at creation (``research_routes.RESEARCH_DISABLED_REGIONS``). The
// two surfaces are gated for DIFFERENT reasons; do not collapse them.
//
// IE went live on Keyword 2026-10-31 (ie_chunk_phrase_index), IT 2026-11-02
// (it_chunk_phrase_index; backend KW_IT_ENABLED). CH / DE / FR went live 2026-10-27.
export const KEYWORD_DISABLED_REGIONS: ReadonlySet<SearchRegion> = new Set(['CN']);

// Per-region disabled tooltip text.  Fallback to DISABLED_REGION_TOOLTIP
// for any region not in this map.
export const DISABLED_REGION_TOOLTIPS: Partial<Record<SearchRegion, string>> = {
  // UK is greyed on COGNI ONLY (its research retrieval hangs the worker).
  // Keyword was opened to all users 2026-10-27, so this tooltip no longer
  // reaches the Keyword surface — only the cogni/research pills.
  UK: 'Updating ...',
  // CN is greyed on the KEYWORD surface only (Compare view not yet wired for zh).
  CN: 'Coming soon (compare)',
  CH: 'Coming soon',
  DE: 'Coming soon',
  FR: 'Coming soon',
  IT: 'Coming soon',
};
export const DISABLED_REGION_TOOLTIP = 'Updating ...';

/**
 * Tooltip to show on a disabled region pill.  Returns the region-specific
 * message when defined, else the generic fallback.
 */
export function getDisabledRegionTooltip(region: SearchRegion): string {
  return DISABLED_REGION_TOOLTIPS[region] ?? DISABLED_REGION_TOOLTIP;
}

/**
 * Disabled regions an admin may still click through to spot-check — the data
 * is merely "Updating ..." (a parser-quality flag), safe to view. UK is
 * intentionally NOT here: its research retrieval currently HANGS the worker
 * (the monolithic UK vector step never returns), so even admins are blocked
 * until it's fixed. The backend rejects UK research too (research_routes).
 */
// Empty since IE / IT launched on Cogni 2026-10-31.
export const ADMIN_BYPASSABLE_REGIONS: ReadonlySet<SearchRegion> = new Set<SearchRegion>();

/**
 * Whether a region pill should render as greyed/non-clickable.
 *
 * Disabled regions (``DISABLED_REGIONS``) are greyed for everyone. Admins may
 * bypass the greyout ONLY for regions in ``ADMIN_BYPASSABLE_REGIONS`` (data is
 * safe-to-spot-check "Updating ..."). UK is hard-disabled — not bypassable —
 * because its research hangs the worker.
 *
 * Default ``isAdmin=false`` so any caller that hasn't been threaded the
 * value still gets the safe (greyed) behaviour.
 */
export function isRegionDisabled(region: SearchRegion, isAdmin: boolean = false): boolean {
  if (!DISABLED_REGIONS.has(region)) return false;
  if (isAdmin && ADMIN_BYPASSABLE_REGIONS.has(region)) return false;
  return true;
}

/**
 * Regions an admin may KEYWORD-search even though they're greyed for everyone.
 * Deliberately SEPARATE from ADMIN_BYPASSABLE_REGIONS (cogni/research).
 *
 * EMPTY since 2026-10-27. It held UK, as the admin-only door to UK keyword
 * search while that surface was greyed for parse quality; UK is now open to
 * everyone (see KEYWORD_DISABLED_REGIONS above), so the bypass is unreachable
 * for it — ``isKeywordRegionDisabled`` returns false before consulting this set.
 * CH / DE / FR passed through it for their admin E2E and launched to all users
 * 2026-10-27 (out of KEYWORD_DISABLED_REGIONS), so it is empty again.
 */
export const KEYWORD_ADMIN_BYPASSABLE_REGIONS: ReadonlySet<SearchRegion> = new Set([]);

/**
 * Whether a region pill should be greyed on the KEYWORD surface.
 * Greyed for everyone if in KEYWORD_DISABLED_REGIONS, EXCEPT admins may click
 * through regions in KEYWORD_ADMIN_BYPASSABLE_REGIONS (safe FTS path).
 */
export function isKeywordRegionDisabled(region: SearchRegion, isAdmin: boolean = false): boolean {
  if (!KEYWORD_DISABLED_REGIONS.has(region)) return false;
  if (isAdmin && KEYWORD_ADMIN_BYPASSABLE_REGIONS.has(region)) return false;
  return true;
}

// ── Region → available category options ────────────────────────────────
export const REGION_CATEGORY_MAP: Record<SearchRegion, { key: string; label: string }[]> = {
  US: [
    { key: 'nda', label: 'NDA' },
    { key: 'bla', label: 'BLA' },
    { key: 'nda_ag', label: 'NDA Authorized Generic' },
    { key: 'anda', label: 'ANDA (Generic)' },
    { key: 'otc', label: 'OTC Monograph' },
    { key: 'homeopathic', label: 'Homeopathic' },
    { key: 'animal', label: 'Animal Drugs (NADA)' },
    { key: 'other', label: 'Other Unapproved' },
  ],
  US_FDA: [
    { key: 'nda', label: 'NDA' },
    { key: 'bla', label: 'BLA' },
    { key: 'anda', label: 'ANDA (Generic)' },
  ],
  EU: [
    { key: 'eu_authorised', label: 'EU Centralised (Authorised)' },
  ],
  UK: [
    { key: 'uk_authorised', label: 'UK Authorised' },
  ],
  // CA/AU/JP expose NO marketing-category facet. CA/AU's marketing_status was a
  // hardcoded literal ("Marketed"), not a source-derived category, and JP
  // (PMDA) has no NDA/BLA-style classification at all (status is NULL). We don't
  // surface a filter pill for a fabricated/absent value — selecting these
  // regions applies no marketing-category filter. Empty arrays keep the
  // Record<SearchRegion, …> exhaustive for the TS compiler.
  CA: [],
  AU: [],
  JP: [],
  // CN (zh_pdf) exposes no marketing-category facet — empty keeps the
  // Record<SearchRegion, …> exhaustive for the TS compiler.
  CN: [],
  CH: [],
  DE: [],
  FR: [],
  IE: [],
  IT: [],
};

// ── Default categories auto-selected when a region is first checked ────
// NOTE: ``nda_ag`` (NDA Authorized Generic) is intentionally excluded —
// users can opt in via the filter UI, but it's off by default to keep
// search results focused on innovator labels.
export const REGION_DEFAULT_CATEGORIES: Record<SearchRegion, string[]> = {
  US: ['nda', 'bla'],
  US_FDA: ['nda', 'bla'],
  EU: ['eu_authorised'],
  UK: ['uk_authorised'],
  // CA/AU/JP have no marketing-category facet (see REGION_CATEGORY_MAP), so no
  // defaults auto-select; empty arrays keep the Record exhaustive.
  CA: [],
  AU: [],
  JP: [],
  // CN has no marketing-category facet (see REGION_CATEGORY_MAP).
  CN: [],
  CH: [],
  DE: [],
  FR: [],
  IE: [],
  IT: [],
};

// ── Global defaults ────────────────────────────────────────────────────
export const DEFAULT_REGIONS: SearchRegion[] = ['US'];
export const DEFAULT_MARKETING_CATEGORIES: string[] = ['nda', 'bla'];
export const DEFAULT_SEARCH_MODE: SearchModeValue = 'labels';

// ── FDA HA review document-type options ───────────────────────────────
// This is the frontend copy of the backend's canonical SBA priority order.
// Keep request values, full labels, and compact labels together so URL state,
// request bodies, and both SearchFilters layouts cannot drift independently.
export const SBA_DOC_TYPE_OPTIONS: readonly {
  value: SBADocType;
  label: string;
  compactLabel: string;
}[] = [
  { value: 'medical_review', label: 'Medical Review', compactLabel: 'Medical' },
  {
    value: 'clinical_pharmacology_review',
    label: 'Clinical Pharmacology Review',
    compactLabel: 'Clin Pharm',
  },
  { value: 'statistical_review', label: 'Statistical Review', compactLabel: 'Statistical' },
  { value: 'coa_review', label: 'COA Review', compactLabel: 'COA' },
  { value: 'nonclinical_review', label: 'Nonclinical Review', compactLabel: 'Nonclinical' },
  { value: 'summary_review', label: 'Summary Review', compactLabel: 'Summary' },
  {
    value: 'risk_assessment_review',
    label: 'Risk Assessment Review',
    compactLabel: 'Risk Assessment',
  },
  {
    value: 'cross_discipline_review',
    label: 'Cross Discipline Review',
    compactLabel: 'Cross Discipline',
  },
  { value: 'chemistry_review', label: 'Chemistry Review', compactLabel: 'Chemistry' },
  {
    value: 'microbiology_review',
    label: 'Microbiology Review',
    compactLabel: 'Microbiology',
  },
  { value: 'integrated_review', label: 'Integrated Review', compactLabel: 'Integrated' },
  {
    value: 'multi_discipline_review',
    label: 'Multi Discipline Review',
    compactLabel: 'Multi Discipline',
  },
  { value: 'other_review', label: 'Other Review', compactLabel: 'Other' },
];

export const DEFAULT_SBA_DOC_TYPES: SBADocType[] = ['medical_review'];

const SBA_DOC_TYPE_ORDER = new Map<SBADocType, number>(
  SBA_DOC_TYPE_OPTIONS.map((option, index) => [option.value, index]),
);
const SBA_DOC_TYPE_VALUES = new Set<string>(
  SBA_DOC_TYPE_OPTIONS.map((option) => option.value),
);

/**
 * Return only known HA document types, de-duplicated and sorted in canonical
 * priority order. Unlike normalizeSbaDocTypes(), this may return an empty
 * array; request-layer code uses that distinction to refuse an unscoped call.
 */
export function canonicalizeSbaDocTypes(
  values: readonly unknown[] | null | undefined,
): SBADocType[] {
  const unique = new Set<SBADocType>();
  for (const value of values ?? []) {
    if (typeof value === 'string' && SBA_DOC_TYPE_VALUES.has(value)) {
      unique.add(value as SBADocType);
    }
  }
  return [...unique].sort(
    (left, right) =>
      (SBA_DOC_TYPE_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (SBA_DOC_TYPE_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER),
  );
}

/**
 * Normalize a Cogni UI/URL selection. A missing or fully invalid value maps
 * to the Medical Review default; the lower-level SSE hook deliberately uses
 * canonicalizeSbaDocTypes() instead so it never invents request scope.
 */
export function normalizeSbaDocTypes(
  values: readonly unknown[] | null | undefined,
): SBADocType[] {
  const canonical = canonicalizeSbaDocTypes(values);
  return canonical.length > 0 ? canonical : [...DEFAULT_SBA_DOC_TYPES];
}

/** Toggle one HA type while preserving the at-least-one selection invariant. */
export function toggleSbaDocType(
  values: readonly unknown[] | null | undefined,
  value: SBADocType,
): SBADocType[] {
  const current = normalizeSbaDocTypes(values);
  if (current.includes(value)) {
    return current.length === 1
      ? current
      : canonicalizeSbaDocTypes(current.filter((item) => item !== value));
  }
  return canonicalizeSbaDocTypes([...current, value]);
}

// ── FDA-letter filter options ──────────────────────────────────────────
// Pill-render constants for the three letter-filter rows that appear when
// the user picks the FDA Letters corpus.  ``as const`` so the pill-render
// code in ``SearchFilters`` (Task 8) can narrow the ``key`` to its precise
// literal union for type-safe toggle handlers.  Tooltip text is empty for
// the self-explanatory roles; the comparator/mentioned tooltips spell out
// the LLM classifier semantics surfaced to the user.
export const LETTER_TYPE_OPTIONS = [
  { key: 'warning', label: 'Warning' },
  { key: 'untitled', label: 'Untitled' },
  { key: 'promotional', label: 'Promotional' },
] as const;

export const LETTER_CORPUS_OPTIONS = [
  { key: 'ad_promo', label: 'AD Promo' },
  { key: 'other', label: 'Other' },
] as const;

export const LETTER_ROLE_OPTIONS = [
  { key: 'target', label: 'Target', tooltip: '' },
  {
    key: 'comparator', label: 'Comparator',
    tooltip: 'The drug is referenced in an approval context — e.g. ' +
      '"approved under NDA 21-282".',
  },
  {
    key: 'mentioned', label: 'Mentioned',
    tooltip: 'The drug appears in the letter body without explicit ' +
      'approval phrasing.',
  },
  { key: 'unclassified', label: 'Unclassified', tooltip: '' },
] as const;

// Division pill options. ``fda_letters.division`` is populated for the
// modern OPDP / OCBQ enforcement-letter feeds (1.4k + 155 letters on
// staging respectively); everything else (legacy ``office_1..11`` slugs,
// the literal string ``"other"``, NULLs) folds into the UI-visible
// ``Other`` bucket. The ``Other`` key is intentionally capitalized to
// match the literal label so the SQL clause builder can treat it as the
// "not OPDP and not OCBQ (or NULL)" sentinel without a second lookup.
export const LETTER_DIVISION_OPTIONS = [
  { key: 'OPDP', label: 'OPDP' },
  { key: 'OCBQ', label: 'OCBQ' },
  { key: 'Other', label: 'Other' },
] as const;

// Defaults: Warning+Untitled; AD Promo+Other (both — letter_corpus
// ships empty, so AD-Promo-only would zero the default search; the
// populate-later effort flips this to ['ad_promo']); Target+Unclassified;
// all three Division buckets selected (matches the existing pill UX
// where "everything on" == no filter applied).
//
// Typed as ``string[]`` (not ``readonly`` from ``as const``) to match the
// existing ``DEFAULT_MARKETING_CATEGORIES`` pattern — assignable to the
// ``SearchFilterOptions`` array fields without widening gymnastics, and
// every read returns a fresh array reference (the ``const`` binding is on
// the variable, the value is a literal array each module load).
export const DEFAULT_LETTER_TYPES: string[] = ['warning', 'untitled'];
export const DEFAULT_LETTER_CORPUS: string[] = ['ad_promo', 'other'];
export const DEFAULT_LETTER_ROLES: string[] = ['target', 'unclassified'];
export const DEFAULT_LETTER_DIVISIONS: string[] = ['OPDP', 'OCBQ', 'Other'];

export const DEFAULT_FILTERS: SearchFilterOptions = {
  regions: DEFAULT_REGIONS,
  marketingCategories: DEFAULT_MARKETING_CATEGORIES,
  searchMode: DEFAULT_SEARCH_MODE,
  letterTypes: DEFAULT_LETTER_TYPES,
  letterCorpus: DEFAULT_LETTER_CORPUS,
  letterRoles: DEFAULT_LETTER_ROLES,
  letterDivisions: DEFAULT_LETTER_DIVISIONS,
};

// ── Keyword search category mapping ────────────────────────────────────
// The keyword search backend filters against drugs.marketing_status, which
// stores human-readable strings, not the lowercase frontend keys.
export const KW_CATEGORY_MAP: Record<string, string> = {
  nda:           'NDA',
  bla:           'BLA',
  anda:          'ANDA',
  nda_ag:        'NDA authorized generic',
  eu_authorised: 'Authorised',
  uk_authorised: 'Authorised',
  // No ca_marketed / au_marketed / jp_authorised: those category pills were
  // removed 2026-06-24 (fabricated/absent marketing_status — see
  // REGION_CATEGORY_MAP and NON_US_CATEGORIES in SearchFilters.tsx).
  otc:           'OTC Monograph Drug',
  homeopathic:   'unapproved homeopathic',
  animal:        'NADA',
  other:         'unapproved drug other',
};

// ── Per-surface doc-type availability (B2) ─────────────────────────────
// Which SearchModeValues are RENDERED for each surface.  Admin-gating is
// applied inside surfaceModes().  draft_review is Cogni/admin-only.
// multi_review is RENDERED for everyone and admin-gated in isModeEnabled
// (US MDR/SBA, EU EPAR, JP PMDA) — greyed, not hidden, for non-admins.
// label_negotiations is authenticated (not admin-only) on Investigator.
export type Surface = 'cogni' | 'investigator' | 'keyword';

// ── Admin-only regions (HIDDEN for non-admins on every surface) ────────
// CN (Chinese zh_pdf) was admin-dogfood here until 2026-08-31, when it was
// promoted to GA for all users (corpus fully backfilled + retrieval validated).
// The set is now empty. NOTE: CN stays GREYED on the Keyword surface for
// everyone via KEYWORD_DISABLED_REGIONS (Compare view isn't wired for zh yet);
// un-hiding the pill here does NOT un-grey keyword. The backend CN gates were
// opened in lockstep 2026-08-31 (search_routes_v2 SEARCH_ADMIN_ONLY_REGIONS,
// research_routes RESEARCH_ADMIN_ONLY_REGIONS, unified_chat zh gate, and the
// ZH_PDF_ADMIN_ONLY env for the viewer).
// IE / IT are admin-only (IE_PDF_ADMIN_ONLY / IT_PDF_ADMIN_ONLY on the backend);
// on Cogni + Keyword they render GREYED "Coming soon" for everyone instead of
// hidden (isRegionVisible). CH / DE / FR left this set at the 2026-10-27 launch
// (CH_PDF / DE_PDF / FR_HTML _ADMIN_ONLY default 0 in compose).
// US_FDA (US-Drugs@FDA) is admin-only too (US_FDA_ADMIN_ONLY) but HIDDEN, not
// greyed, for non-admins on every surface (isUsFdaVisible / isRegionVisible).
// IE / IT left this set 2026-10-31 (IE_PDF / IT_PDF _ADMIN_ONLY default 0 in compose).
export const ADMIN_ONLY_REGIONS: ReadonlySet<SearchRegion> = new Set<SearchRegion>(['US_FDA']);

/**
 * Whether the US-Drugs@FDA region (and its request-path selectors) is live for
 * this user. Mirrors the backend gate (`US_FDA_ADMIN_ONLY` -> admin_only_regions()).
 * Removing 'US_FDA' from ADMIN_ONLY_REGIONS opens it to everyone.
 */
export function isUsFdaVisible(isAdmin: boolean): boolean {
  return isAdmin || !ADMIN_ONLY_REGIONS.has('US_FDA');
}

/**
 * Display label for a region code (display only: API values, cache keys and
 * stored data stay 'US'). Once US-Drugs@FDA is visible the legacy DailyMed
 * region reads "US-DailyMed" and US_FDA reads its REGION_OPTIONS label; until
 * then the caller's `legacy` label (default: the raw code) is returned
 * unchanged, so today's surfaces stay byte-identical.
 */
export function regionDisplayLabel(
  code: string,
  usFdaVisible: boolean,
  legacy: string = code,
): string {
  if (!usFdaVisible) return legacy;
  if (code === 'US') return 'US-DailyMed';
  if (code === 'US_FDA') return REGION_OPTIONS.find((o) => o.value === 'US_FDA')?.label ?? legacy;
  return legacy;
}

/**
 * Whether a region pill should be RENDERED at all for a given surface/user.
 * Distinct from ``isRegionDisabled`` (which greys a rendered pill): admin-only
 * regions (``ADMIN_ONLY_REGIONS``) render ONLY on the Investigator surface for
 * platform admins; every other surface/user hides them entirely. All other
 * regions are always visible (their greyed state is decided separately).
 */
export function isRegionVisible(
  region: SearchRegion,
  surface: Surface,
  isAdmin: boolean,
): boolean {
  // US-Drugs@FDA: hidden (not greyed) for non-admins on every surface.
  if (region === 'US_FDA') return isUsFdaVisible(isAdmin);
  // Coming-soon regions (IE/IT; CH/DE/FR launched 2026-10-27) RENDER on Cogni + Keyword for every
  // user; whether the pill is clickable is decided by isRegionDisabled /
  // isKeywordRegionDisabled ("Coming soon" greyout, admin bypass on Cogni).
  if (ADMIN_ONLY_REGIONS.has(region) && surface !== 'investigator') return true;
  // CN (Chinese zh_pdf) is admin-dogfood across ALL THREE tools now: active on
  // Cogni + Investigator, and RENDERED-BUT-GREYED on Keyword (compare view isn't
  // wired for zh yet — see KEYWORD_DISABLED_REGIONS). Still hidden entirely for
  // non-admins on every surface until the general rollout.
  // Investigator: IE / IT for admins only (CH / DE / FR launched 2026-10-27) (research reads them
  // through LABEL_FAMILY_MAP; RESEARCH_ADMIN_ONLY_REGIONS gates non-admins).
  if (ADMIN_ONLY_REGIONS.has(region)) return isAdmin;
  return true;
}

/** ``REGION_OPTIONS`` filtered to the pills visible for a surface/admin context. */
export function visibleRegionOptions(
  surface: Surface,
  isAdmin: boolean,
): { value: SearchRegion; label: string }[] {
  return REGION_OPTIONS.filter((o) => isRegionVisible(o.value, surface, isAdmin));
}

/** Keyword-surface region chips: ``visibleRegionOptions('keyword', isAdmin)``
 *  with ``soon`` = not clickable for this user, stable-partitioned so the
 *  "soon" chips come last. ``REGION_OPTIONS`` order is left alone (the
 *  registry-parity test reads it). */
export function keywordRegionOptions(
  isAdmin: boolean,
): { value: SearchRegion; label: string; soon: boolean }[] {
  const all = visibleRegionOptions('keyword', isAdmin).map((o) => ({
    ...o,
    soon: isKeywordRegionDisabled(o.value, isAdmin),
  }));
  return [...all.filter((o) => !o.soon), ...all.filter((o) => o.soon)];
}

/** Default tooltip on a greyed Document Type pill — the mode exists but isn't
 *  ready for this context yet (region not supported, feature pre-GA). */
export const MODE_DISABLED_TOOLTIP = 'Coming soon ...';

/** Tooltip when a Label Negotiations control is greyed because the TENANT has
 *  no drafts. Distinct from MODE_DISABLED_TOOLTIP: nothing is "coming soon"
 *  here — the feature is live and simply has no data to search yet, which is
 *  fixed by uploading a draft, not by waiting for a release. */
export const NO_LABEL_NEGOTIATIONS_TOOLTIP =
  'No label negotiations uploaded for your organization';

/** Tooltip on the non-Labels Document Type pills while the Keyword surface is
 *  in Instant mode — the chip search resolves labels only. */
export const INSTANT_LABELS_ONLY_TOOLTIP = 'Instant searches labels only';

// 'ci' (competitive intel) is served by cogni + investigator only (see
// backend/app/corpus/capability.py SERVES — keyword has no ci cells).
//
// ARRAY ORDER IS THE PILL RENDER ORDER in the Document Type row. Cogni and
// Investigator lead with the four user-facing pills in the order requested
// 2026-08-16: Labels, FDA Approval Letters, FDA Enforcement Letters, Label
// Negotiations (draft_review on Cogni / label_negotiations on Investigator —
// same visible label, different mode value). 'ci' and 'multi_review' trail
// them. Keyword is untouched (labels + fda_letters only).
export const SURFACE_DOC_TYPES: Record<Surface, SearchModeValue[]> = {
  cogni: ['labels', 'approval_letters', 'fda_letters', 'draft_review', 'ci', 'multi_review'],
  investigator: ['labels', 'approval_letters', 'fda_letters', 'label_negotiations', 'ci', 'multi_review'],
  keyword: ['labels', 'fda_letters'],
};

/**
 * The modes actually RENDERED for a surface after admin-gating.
 * draft_review is admin-only on any surface that includes it.
 * label_negotiations is authenticated-gated (not admin-only): available to all
 * authenticated users on Investigator (ProtectedRoute ensures auth).
 * Non-admins get labels + fda_letters + label_negotiations (on Investigator)
 * + approval_letters + multi_review.
 *
 * ONE MODE IS RENDER-FOR-ALL, ADMIN-GATED IN isModeEnabled — do NOT
 * "restore" an isAdmin check here for it:
 *   - approval_letters — always rendered, greyed for non-admins with the shared "Coming soon ...".
 *     Admin-restricted as a staged rollout. (The original reason — ~83% of the
 *     prod corpus unchunked — was RESOLVED 2026-08-17: the artifacts backfill
 *     took coverage to 20,002/20,002 with 0 null embeddings. The gate now
 *     stands purely as a product decision, so removing it is a product call,
 *     not a data-readiness one.)
 *
 * multi_review ("HA Reviews") is admin-gated HERE as of 2026-08-17-b — hidden,
 * not greyed. It previously shared the approval_letters shape; it no longer
 * does. Admins keep the per-region rules (Cogni US; Investigator US/EU/JP).
 * Both are UX GATES ONLY. The backend routes (fda_approval_letter_routes.py,
 * research_routes.py, sba_routes.py) check the server-side feature flag where
 * one exists but NEVER admin, so a hand-crafted request from any
 * authenticated non-admin still reaches either corpus.
 *
 * 'ci' is different — genuinely admin-only HERE (hidden, not greyed) AND
 * behind the build-time VITE_CI_CORPUS_ENABLED flag (isCompetitiveIntelEnabled),
 * which mirrors the backend CI_CORPUS_ENABLED kill switch so a build with the
 * flag off never renders an entry point the server would 404.
 */
export function surfaceModes(surface: Surface, isAdmin: boolean): SearchModeValue[] {
  const all = SURFACE_DOC_TYPES[surface];
  return all.filter((m) => {
    // draft_review is admin-only; label_negotiations is authenticated (not
    // admin-gated), so it falls through to `true` below.
    if (m === 'draft_review') return isAdmin;
    // multi_review (displayed "HA Reviews") is RENDERED FOR EVERYONE as of
    // 2026-09-01 — the admin gate that stood here from 2026-08-17-b is gone,
    // on both Cogni and Investigator, by product decision.
    //
    // The 2026-08-17-b reason for hiding it was that the pill "was advertising
    // a corpus non-admins cannot open". That no longer holds: non-admins CAN
    // open it now, because the matching gate in ``isModeEnabled`` was removed
    // in the same change. Rendering it while it is clickable is coherent;
    // rendering it while it was not is what was confusing.
    //
    // No isAdmin branch is needed at all — it falls through to `true` below.
    // The per-region rules in ``isModeEnabled`` still apply to everyone
    // (Cogni: US only; Investigator: US / EU / JP), and the server-side region
    // rules in research_routes.py (CN admin-only, disabled regions 422) remain
    // the authoritative enforcement.
    if (m === 'ci') return isAdmin && isCompetitiveIntelEnabled();
    if (m === 'approval_letters') {
      // NOT admin-gated here (2026-08-17). Visibility is deliberately open so
      // non-admins SEE the pill greyed with "Coming soon ..." rather than not
      // knowing the corpus exists. The admin restriction moved to isModeEnabled
      // — rendered for all, clickable only for admins.
      return true;
    }
    return true;
  });
}

/**
 * Whether a rendered mode is CLICKABLE (not disabled) for the given context.
 * Returns false if the mode is not even in surfaceModes (shouldn't be shown).
 *
 * Rules:
 *  - multi_review: ADMIN-ONLY, then per-region. Cogni: regions exactly ['US'].
 *    Investigator: exactly ['US'], ['EU'] (EPAR) or ['JP'] (PMDA). Non-admins
 *    are never enabled on any region — they see the pill rendered but greyed
 *    with a "coming soon" hover text (see surfaceModes above).
 *  - draft_review: admin-only (already filtered by surfaceModes for non-admins,
 *    but we return false here too for non-admins in case caller bypasses)
 *  - ci: region-independent by construction — no branch below inspects
 *    ``regions`` for it, so once it clears the surfaceModes (admin+flag)
 *    gate above it falls through to the unconditional `true` at the
 *    bottom regardless of which/how many regions are selected. The
 *    backend mirrors this: corpus='ci' rejects a non-empty region/regions
 *    with 422 rather than filtering by it (see ResearchPanel's ci submit
 *    body, which omits both fields).
 *  - draft_review / label_negotiations: additionally require the caller's
 *    tenant to own at least one draft (``hasLabelNegotiations``) — see below.
 *  - All others that are rendered: enabled
 *
 * ``hasLabelNegotiations`` comes from ``/auth/sync``'s
 * ``has_label_negotiations`` via ``useHasLabelNegotiations``. It gates BOTH
 * searchModes that render the visible label "Label Negotiations":
 * ``draft_review`` (Cogni) and ``label_negotiations`` (Investigator). Without
 * tenant drafts both can only ever return nothing.
 *
 * It DEFAULTS TO TRUE on purpose. A call site that forgets to thread it
 * degrades to the pre-existing behaviour (control offered) rather than hiding
 * a paid feature from a tenant that does have drafts — the worse of the two
 * failure modes. Every real call site passes it explicitly.
 */
export function isModeEnabled(
  mode: SearchModeValue,
  surface: Surface,
  regions: string[],
  isAdmin: boolean,
  hasLabelNegotiations: boolean = true,
): boolean {
  // Must be a rendered mode for this surface + admin combo
  if (!surfaceModes(surface, isAdmin).includes(mode)) return false;

  if (mode === 'multi_review') {
    // OPEN TO ALL AUTHENTICATED USERS as of 2026-09-01 (product decision).
    // The `if (!isAdmin) return false;` that stood here from 2026-08-17 is
    // deliberately gone — do NOT reinstate it without also re-hiding the pill
    // in ``surfaceModes``, or non-admins get an advertised-but-dead control,
    // which is exactly the state 2026-08-17-b was trying to fix.
    //
    // The region rules below now apply to EVERYONE, not just admins. They are
    // a UX gate; the authoritative region enforcement is server-side in
    // ``research_routes._validate_regions`` (CN 403 for non-admins, disabled
    // regions 422). The multi_review corpus itself takes no admin dependency
    // on the backend — verified 2026-09-01 in research_routes.py and
    // sba_routes.py — so this change advertises access that already existed.
    // US-Drugs@FDA visible: the US branch is served by US_FDA (letters/reviews
    // come from us_fda_submissions); EU and JP are unchanged.
    if (isUsReviewScope(regions, isAdmin)) return surface === 'cogni' || surface === 'investigator';
    if (regions.length !== 1) return false;
    if (regions[0] === 'EU') return surface === 'investigator';   // EPAR — Investigator only
    if (regions[0] === 'JP') return surface === 'investigator';   // PMDA supplementary docs — Investigator only
    return false;
  }
  if (mode === 'draft_review') {
    // Cogni's "Label Negotiations" pill. Admin-only AND tenant-data-gated.
    return isAdmin && hasLabelNegotiations;
  }
  if (mode === 'label_negotiations') {
    // Investigator's "Label Negotiations" pill. Authenticated (not admin-only),
    // US-only, and tenant-data-gated. The data gate is ADDITIVE to the region
    // gate, never an override — US-only still holds for a tenant with drafts.
    return (
      surface === 'investigator'
      && regions.length === 1
      && regions[0] === 'US'
      && hasLabelNegotiations
    );
  }
  if (mode === 'approval_letters') {
    // OPEN TO ALL AUTHENTICATED USERS as of 2026-09-01 (product decision).
    // The `isAdmin &&` that stood here from 2026-08-17 is deliberately gone.
    //
    // The staged-rollout reason expired first (the ~83% unchunked corpus was
    // backfilled to 20,002/20,002 with 0 null embeddings on 2026-08-17), and
    // the server-side gates were removed in the same change as this one —
    // research_routes._require_approval_letter_admin and the /search admin
    // check in fda_approval_letter_routes. Leaving this line while those went
    // would have hidden a corpus users could reach, which is the mismatch
    // multi_review just had.
    //
    // US-only still holds for EVERYONE: the request never carries a region,
    // so any other selection would silently mislead about what was searched.
    return isUsReviewScope(regions, isAdmin);
  }
  return true;
}

/**
 * Single predicate for "this selection is the US letters/reviews scope":
 * exactly US_FDA (visible viewers) / US (others), or, for visible viewers only,
 * NO region (Spec D10: default scope serves letters/reviews from US_FDA).
 * Non-visible viewers with no region keep today's behaviour (not this scope).
 * Used by mode gating, the doc-type controls and the Investigator submit body.
 */
export function isUsReviewScope(regions: readonly string[], isAdmin: boolean): boolean {
  const visible = isUsFdaVisible(isAdmin);
  if (regions.length === 0) return visible;
  return regions.length === 1 && regions[0] === (visible ? 'US_FDA' : 'US');
}

// ── Helpers ────────────────────────────────────────────────────────────
/** Get all category options visible for a given set of regions. */
export function getCategoriesForRegions(regions: SearchRegion[]): { key: string; label: string }[] {
  return regions.flatMap((r) => REGION_CATEGORY_MAP[r]);
}

/** Convert regions array to the single-string the backend expects. */
export function regionsToBackendRegion(regions: SearchRegion[]): string | undefined {
  return regions.length === 1 ? regions[0] : undefined;
}

/**
 * Group selected marketing-category pills by the region each one belongs to
 * (``REGION_CATEGORY_MAP``), mapped through the frontend→DB status map
 * (``KW_CATEGORY_MAP``). A region with no selected pill is simply absent
 * from the result — the backend treats an absent region as facetless and
 * passes it through untouched, rather than ANDing a global status across
 * every region (the historical region-blind marketing-status bug).
 *
 * Extracted from the loop that used to live inline in
 * ``keywordRequest.ts:93-102`` (spec T3/AC5) so the chip request builder can
 * call the SAME implementation rather than a second copy — duplicating this
 * loop is what re-broke the region-blind bug once already.
 */
export function buildCategoriesByRegion(
  regions: readonly SearchRegion[],
  marketingCategories: readonly string[],
): Record<string, string[]> {
  const catsByRegion: Record<string, string[]> = {};
  for (const region of regions) {
    const regionKeys = new Set((REGION_CATEGORY_MAP[region] ?? []).map((c) => c.key));
    const statuses = Array.from(new Set(
      marketingCategories
        .filter((k) => regionKeys.has(k))
        .map((k) => KW_CATEGORY_MAP[k])
        .filter((v): v is string => Boolean(v)),
    ));
    if (statuses.length > 0) catsByRegion[region] = statuses;
  }
  return catsByRegion;
}

/** Toggle a region in a SearchFilterOptions, auto-adjusting categories.
 *
 * Unchecking a region also drops every category that belongs to it; checking
 * a region auto-adds its default categories. ALWAYS returns a SearchFilterOptions
 * — previous versions returned ``null`` when unchecking the last region (to
 * enforce "at least one region required"), which silently dropped the user
 * click on the floor and left the UI checkbox visually checked while state
 * never updated. An empty ``regions`` array means "no region filter" and is
 * sent to the backend as such; the user can re-add a region at any time.
 */
export function toggleRegion(
  current: SearchFilterOptions,
  region: SearchRegion,
): SearchFilterOptions {
  const isSelected = current.regions.includes(region);

  if (isSelected) {
    const nextRegions = current.regions.filter((r) => r !== region);
    // Keep category keys still owned by a remaining region (e.g. US + US_FDA
    // share NDA/BLA/ANDA) so removal order does not matter.
    const stillOwned = new Set(nextRegions.flatMap((r) => REGION_CATEGORY_MAP[r].map((c) => c.key)));
    const removedKeys = new Set(
      REGION_CATEGORY_MAP[region].map((c) => c.key).filter((k) => !stillOwned.has(k)),
    );
    return {
      ...current,
      regions: nextRegions,
      marketingCategories: current.marketingCategories.filter((k) => !removedKeys.has(k)),
    };
  }

  const defaults = REGION_DEFAULT_CATEGORIES[region];
  return {
    ...current,
    regions: [...current.regions, region],
    marketingCategories: [
      ...current.marketingCategories,
      ...defaults.filter((k) => !current.marketingCategories.includes(k)),
    ],
  };
}

// Cogni + Keyword stopgap (2026-11-02): searched alone. Cogni: mixing a
// native-language region with any other region times out on that region's
// vector index on prod (DE's is 22 GB) and the whole search fails. Keyword:
// multi-region native search was never designed.
// ponytail: UI-only guard; the backend still accepts a mixed request.
// Swiss → Italy in pill order, IE included: none of them is built for a
// multi-region search yet.
export const COGNI_SOLO_REGIONS: ReadonlySet<string> = new Set(['CH', 'DE', 'FR', 'IE', 'IT']);

/** ``toggleRegion`` plus the Cogni solo rule: adding a solo region clears the
 *  others, and adding any region clears a selected solo region. */
export function toggleRegionCogni(
  current: SearchFilterOptions,
  region: SearchRegion,
): SearchFilterOptions {
  let next = toggleRegion(current, region);
  if (current.regions.includes(region)) return next;
  for (const r of current.regions) {
    if (COGNI_SOLO_REGIONS.has(region) || COGNI_SOLO_REGIONS.has(r)) next = toggleRegion(next, r);
  }
  return next;
}

// ── Keyword "Search in" (admin-only until K4; spec 2026-10-16 §6/§6a) ────
// The admin multi-select over what one keyword search covers. Labels is the
// region-dispatched label corpora; every other value is a document corpus
// key sent as-is in ``sources`` (with ``document_engine: true``). K1b ships
// Enforcement Letters; FDA Reviews / Approval Letters arrive at K2b/K3b.
export type KeywordDocSource = 'labels' | 'fda_letter' | 'fda_approval_letter';

export const KEYWORD_DOC_SOURCE_OPTIONS: { value: KeywordDocSource; label: string }[] = [
  { value: 'labels', label: 'Labels' },
  { value: 'fda_letter', label: 'Enforcement Letters' },
  { value: 'fda_approval_letter', label: 'Approval Letters' },
];

export const KEYWORD_DOC_SOURCE_DISABLED_TOOLTIP =
  'Enforcement Letters are US documents — add US (or clear the region filter) to search them';
export const KEYWORD_APPROVAL_LETTER_DISABLED_TOOLTIP =
  'Approval Letters are searched only when US is the only selected region';

/** Spec §6 region rule. Labels always; Enforcement Letters whenever US is
 *  among the selected regions or no region filter is set. A disabled option
 *  is also deselected by the caller (``enabledKeywordDocSources``). */
export function keywordDocSourceEnabled(opt: KeywordDocSource, regions: SearchRegion[]): boolean {
  if (opt === 'labels') return true;
  // Approval Letters: Cogni's rule exactly -- enabled only for ['US'] (never [])
  // the backend adds no region rule.
  if (opt === 'fda_approval_letter') {
    return regions.length === 1 && regions[0] === 'US';
  }
  return regions.length === 0 || regions.includes('US');
}

/** ``selected`` minus the options the regions disable; never empty (falls
 *  back to Labels, which is always enabled). */
export function enabledKeywordDocSources(
  selected: KeywordDocSource[], regions: SearchRegion[],
): KeywordDocSource[] {
  const kept = selected.filter((s) => keywordDocSourceEnabled(s, regions));
  return kept.length > 0 ? kept : ['labels'];
}
